/**
 * Surface detection for footstep audio variation.
 *
 * Raycasts straight down from the player position to determine what surface
 * type they're standing on. Material names or userData.surfaceType on the
 * hit mesh are mapped to one of the six surface categories.
 */

import * as THREE from 'three';

export type SurfaceType = 'concrete' | 'metal' | 'wood' | 'sand' | 'tile' | 'gravel';

/** Reusable raycaster pointing straight down. */
const downRay = new THREE.Raycaster();
const DOWN = new THREE.Vector3(0, -1, 0);
const SURFACE_CACHE_MS = 100;

type SurfaceCacheEntry = {
  key: string;
  surface: SurfaceType;
  timeMs: number;
};

let surfaceCache = new WeakMap<THREE.Scene, SurfaceCacheEntry>();

/** Map material name prefixes / keywords to surface types. */
const MATERIAL_SURFACE_MAP: Array<{ pattern: string; surface: SurfaceType }> = [
  { pattern: 'metal', surface: 'metal' },
  { pattern: 'steel', surface: 'metal' },
  { pattern: 'iron', surface: 'metal' },
  { pattern: 'grate', surface: 'metal' },
  { pattern: 'vent', surface: 'metal' },
  { pattern: 'wood', surface: 'wood' },
  { pattern: 'plank', surface: 'wood' },
  { pattern: 'crate', surface: 'wood' },
  { pattern: 'barrel', surface: 'wood' },
  { pattern: 'sand', surface: 'sand' },
  { pattern: 'dirt', surface: 'sand' },
  { pattern: 'ground', surface: 'sand' },
  { pattern: 'grass', surface: 'sand' },
  { pattern: 'tile', surface: 'tile' },
  { pattern: 'marble', surface: 'tile' },
  { pattern: 'ceramic', surface: 'tile' },
  { pattern: 'linoleum', surface: 'tile' },
  { pattern: 'gravel', surface: 'gravel' },
  { pattern: 'rock', surface: 'gravel' },
  { pattern: 'rubble', surface: 'gravel' },
  { pattern: 'stone', surface: 'gravel' },
  { pattern: 'concrete', surface: 'concrete' },
  { pattern: 'cement', surface: 'concrete' },
  { pattern: 'asphalt', surface: 'concrete' },
  { pattern: 'floor', surface: 'concrete' },
];

/**
 * Determine the surface type under the given world position.
 *
 * @param scene - The Three.js scene to raycast into.
 * @param position - The player's world-space position (feet level).
 * @returns The detected surface type, defaulting to 'concrete'.
 */
export function detectSurface(
  scene: THREE.Scene,
  position: THREE.Vector3,
  nowMs: number = getNowMs(),
): SurfaceType {
  const cacheKey = surfaceCacheKey(position);
  const cached = surfaceCache.get(scene);
  if (cached && cached.key === cacheKey && nowMs >= cached.timeMs && nowMs - cached.timeMs <= SURFACE_CACHE_MS) {
    return cached.surface;
  }

  const surface = detectSurfaceUncached(scene, position);
  surfaceCache.set(scene, { key: cacheKey, surface, timeMs: nowMs });
  return surface;
}

export function clearSurfaceCache(): void {
  surfaceCache = new WeakMap<THREE.Scene, SurfaceCacheEntry>();
}

function detectSurfaceUncached(
  scene: THREE.Scene,
  position: THREE.Vector3,
): SurfaceType {
  // Cast from slightly above the player's feet straight down
  const origin = position.clone();
  origin.y += 0.5;

  downRay.set(origin, DOWN);
  downRay.far = 2.0; // Only check 2m below
  downRay.layers.set(0); // Default layer only

  const hits = downRay.intersectObjects(scene.children, true);

  for (const hit of hits) {
    if (!hit.object || !hit.object.visible) continue;
    // Skip particles, effects, and batched-away objects
    if (hit.object.userData?.isEffect || hit.object.userData?.batchedAway) continue;

    // Priority 1: explicit surfaceType in userData
    const userData = hit.object.userData;
    if (userData && typeof userData.surfaceType === 'string') {
      const explicit = userData.surfaceType.toLowerCase();
      if (isValidSurface(explicit)) return explicit as SurfaceType;
    }

    // Priority 2: material name matching
    const mesh = hit.object as THREE.Mesh;
    if (mesh.material) {
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of materials) {
        const name = (mat.name || '').toLowerCase();
        if (name) {
          const matched = matchMaterialName(name);
          if (matched) return matched;
        }
      }
    }

    // Priority 3: object name matching
    const objName = (hit.object.name || '').toLowerCase();
    if (objName) {
      const matched = matchMaterialName(objName);
      if (matched) return matched;
    }
  }

  // Default surface
  return 'concrete';
}

function surfaceCacheKey(position: THREE.Vector3): string {
  const q = (v: number) => Math.round(v * 4);
  return `${q(position.x)}:${q(position.y)}:${q(position.z)}`;
}

function getNowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function matchMaterialName(name: string): SurfaceType | null {
  for (const { pattern, surface } of MATERIAL_SURFACE_MAP) {
    if (name.includes(pattern)) return surface;
  }
  return null;
}

function isValidSurface(s: string): boolean {
  return s === 'concrete' || s === 'metal' || s === 'wood' ||
         s === 'sand' || s === 'tile' || s === 'gravel';
}
